import { useMemo, useState } from "react";
import { Alert, AlertTitle, Box, Button, Chip, Snackbar, TextField, Typography } from "@mui/material";
import RichTextEditor from "./RichTextEditor";
import PostPreview from "./PostPreview";
import { useBlog, useCategories, useCategoryAdmin } from "./Blog.hooks";
import { blogPostUrl } from "./Blog.constants";

const wordStats = (html = "") => {
  const text = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const words = text ? text.split(" ").length : 0;
  return { words, minutes: Math.max(1, Math.ceil(words / 200)) };
};

// The reverse proxy rejects an oversized request itself, so the response is
// HTML with no `message` for us to show — a 413 used to surface as the generic
// "Failed to save" and left the author guessing (reported 2026-07-30).
const errorMessage = (e, fallback) => {
  if (e?.response?.status === 413) {
    return "This post is too large to save. It usually means an image was pasted into the body — remove it and re-insert it with the image button in the toolbar.";
  }
  return e?.response?.data?.message || fallback;
};

// Base64 left in the body is what actually blows the size limit; the editor
// lifts pasted images to S3, but a paste that is still uploading would slip
// through here.
const hasInlineDataImage = (html = "") => /<img[^>]+src="data:/i.test(html);

const BlogEditor = ({ post, role, onSaved, onBack }) => {
  const { createPost, updatePost, submitPost, unpublishPost, uploadCover } = useBlog();
  const { categories, loadCategories } = useCategories();
  const { createCategory } = useCategoryAdmin();
  // A published post whose author already submitted an edit keeps that edit in
  // `pendingChanges` — edit from there, not from the live version.
  const source = { ...(post || {}), ...(post?.pendingChanges || {}) };
  const [form, setForm] = useState({
    title: source.title || "",
    excerpt: source.excerpt || "",
    body: source.body || "",
    coverImage: source.coverImage || "",
    category: source.category || "",
    tags: source.tags || [],
  });
  const [id, setId] = useState(post?.id || null);
  const [tagInput, setTagInput] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [error, setError] = useState("");
  const [published, setPublished] = useState(null); // { slug, at } after a successful publish
  const [copied, setCopied] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  const stats = useMemo(() => wordStats(form.body), [form.body]);

  const persist = async ({ publish = false } = {}) => {
    if (hasInlineDataImage(form.body)) {
      const message =
        "An image in the body is still uploading. Give it a moment and press Save again.";
      setError(message);
      const e = new Error(message);
      e.errorShown = true;
      throw e;
    }
    setSaving(true);
    try {
      const payload = { ...form, publish };
      const saved = id ? await updatePost(id, payload) : await createPost(payload);
      setId(saved.id);
      setDirty(false);
      // Publishing gave no feedback at all — the admin could not tell whether it
      // worked, let alone reach the live article (QA 2026-07-28).
      if (publish && saved?.status === "published") {
        setPublished({ slug: saved.slug, at: Date.now() });
      }
      onSaved?.(saved);
      return saved;
    } catch (e) {
      setError(errorMessage(e, "Failed to save. Please try again."));
      e.errorShown = true;
      throw e;
    } finally { setSaving(false); }
  };

  const onSubmitReview = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const saved = await persist();
      await submitPost(saved.id);
      onBack?.();
    } catch (e) {
      // persist() already set an error message; only set here if submit itself failed
      if (!e.errorShown) setError(e.response?.data?.message || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const addTag = () => {
    const t = tagInput.trim().replace(/^#/, "");
    if (t && !form.tags.includes(`#${t}`)) set("tags", [...form.tags, `#${t}`]);
    setTagInput("");
  };

  // Same action as in the list — an author who is already inside the post
  // should not have to go back just to take it offline.
  const onUnpublish = async () => {
    if (!window.confirm(
      "Take this post off the blog? It goes back to your drafts and needs a new review to be published again."
    )) return;
    try { await unpublishPost(id); onBack?.(); }
    catch (e) { setError(e.response?.data?.message || "Failed to unpublish post."); }
  };

  // Adding a category used to mean leaving the post and opening the category
  // manager (QA 2026-07-27). Creation is admin-only on the backend.
  const onAddCategory = async () => {
    const name = newCategory.trim();
    if (!name || savingCategory) return;
    const existing = categories.find((c) => c.toLowerCase() === name.toLowerCase());
    if (existing) { set("category", existing); setNewCategory(""); setAddingCategory(false); return; }
    setSavingCategory(true);
    try {
      await createCategory(name);
      await loadCategories();
      set("category", name);
      setNewCategory("");
      setAddingCategory(false);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to add category.");
    } finally {
      setSavingCategory(false);
    }
  };

  const onCoverFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setUploadingCover(true);
    try {
      const url = await uploadCover(file);
      if (url) set("coverImage", url);
      else setError("Image upload failed. Please try again.");
    } catch (err) {
      setError(
        err?.response?.status === 413
          ? "That image is too large to upload. Please use a smaller file (under about 5 MB)."
          : err?.response?.data?.message || "Image upload failed. Please try again."
      );
    } finally {
      setUploadingCover(false);
    }
  };

  const isAdmin = role === "admin";
  const status = post?.status;
  // A live post edited by its (non-admin) author: changes go to the moderation
  // queue and the published version stays online until an admin approves.
  const needsApprovalForEdits = !isAdmin && status === "published";
  const editAwaitingReview = needsApprovalForEdits && !!post?.pendingSubmittedAt;
  const editWasRejected =
    needsApprovalForEdits && !post?.pendingSubmittedAt && !!post?.pendingChanges && !!post?.reviewComment;

  // Taken off the blog by an admin: the post is a draft again, and the reason
  // has to be where the author opens it (QA 2026-07-28).
  const wasTakenOffline = status === "draft" && !!post?.unpublishedAt;

  const banner = (() => {
    if (wasTakenOffline) {
      return {
        severity: "warning",
        title: "This post was taken off the blog",
        text: post?.reviewComment
          ? `Reason from the reviewer: ${post.reviewComment}`
          : isAdmin
          ? "It is back in drafts. Publishing it again puts it straight back online."
          : "It is back in your drafts. Submit it for review again to put it back online.",
      };
    }
    if (editAwaitingReview) {
      return { severity: "info", title: "Changes awaiting approval",
        text: "Your edits were sent to an admin. The published version stays online until they are approved." };
    }
    if (editWasRejected) {
      return { severity: "warning", title: "Changes were not approved",
        text: post.reviewComment };
    }
    if (status === "rejected") {
      return { severity: "warning", title: "This post was not published",
        text: post?.reviewComment || "No reason was left by the reviewer." };
    }
    if (status === "pending") {
      return { severity: "info", title: "Awaiting review",
        text: "An admin is reviewing this post. You can still edit it before it is published." };
    }
    if (needsApprovalForEdits) {
      return { severity: "info", title: "This post is live",
        text: "Any change you make has to be approved by an admin before it appears on the blog." };
    }
    return null;
  })();

  return (
    <Box sx={{ display: "flex", gap: 3, p: 3 }}>
      {/* LEFT */}
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Button onClick={onBack} size="small" disabled={saving || submitting}>← Back</Button>
            <Typography variant="h6">{id ? "Edit Post" : "New Post"}</Typography>
            <Typography variant="caption">{stats.words} words · {stats.minutes} min read</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            {isAdmin ? (
              <>
                <Button variant="outlined" onClick={() => setPreview(true)}>Preview</Button>
                <Button variant="outlined" disabled={!dirty || saving} onClick={() => persist()}>Save</Button>
                <Button variant="contained" disabled={saving} onClick={() => persist({ publish: true })}>
                  {dirty ? "Save & Publish" : "Publish"}
                </Button>
              </>
            ) : needsApprovalForEdits ? (
              <>
                <Button variant="outlined" onClick={() => setPreview(true)}>Preview</Button>
                <Button variant="outlined" color="warning" disabled={saving} onClick={onUnpublish}>
                  Unpublish
                </Button>
                <Button variant="contained" disabled={!dirty || saving} onClick={() => persist()}>
                  Submit changes for review
                </Button>
              </>
            ) : (
              <>
                <Button variant="outlined" onClick={() => setPreview(true)}>Preview</Button>
                <Button variant="outlined" disabled={!dirty || saving} onClick={() => persist()}>Save Draft</Button>
                <Button variant="contained" disabled={saving || submitting} onClick={onSubmitReview}>Submit for Review</Button>
              </>
            )}
          </Box>
        </Box>

        {banner && (
          <Alert severity={banner.severity} sx={{ mb: 2 }}>
            <AlertTitle>{banner.title}</AlertTitle>
            {banner.text}
          </Alert>
        )}
        {published && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => { setPublished(null); setCopied(false); }}
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  href={blogPostUrl(published.slug)}
                  target="_blank"
                  rel="noopener noreferrer"
                  disabled={!published.slug}
                >
                  View post
                </Button>
                <Button
                  size="small"
                  disabled={!published.slug}
                  onClick={() => {
                    navigator.clipboard?.writeText(blogPostUrl(published.slug));
                    setCopied(true);
                  }}
                >
                  {copied ? "Copied" : "Copy link"}
                </Button>
              </Box>
            }
          >
            <AlertTitle>Post published</AlertTitle>
            {published.slug ? blogPostUrl(published.slug) : "It is now live on the blog."}
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

        <TextField fullWidth placeholder="Enter your post title…"
          value={form.title} onChange={(e) => set("title", e.target.value)} sx={{ mb: 2 }} />
        <TextField fullWidth multiline minRows={2} placeholder="Write a short excerpt or summary…"
          value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} sx={{ mb: 2 }} />
        <RichTextEditor
          value={form.body}
          onChange={(v) => set("body", v)}
          uploadImage={uploadCover}
          onError={setError}
        />
      </Box>

      {/* RIGHT */}
      <Box sx={{ width: 300 }}>
        <Typography variant="subtitle2">Cover Image</Typography>
        {form.coverImage && (
          <img src={form.coverImage} alt="cover" style={{ width: "100%", borderRadius: 8 }} />
        )}
        {/* The upload button used to disappear as soon as a cover existed, so a
            published post's image could only be replaced by clearing the URL
            field by hand (QA 2026-07-27). */}
        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
          <Button component="label" variant="outlined" fullWidth disabled={uploadingCover}>
            {uploadingCover ? "Uploading…" : form.coverImage ? "Change image" : "Click to upload"}
            <input hidden type="file" accept="image/*" onChange={onCoverFile} />
          </Button>
          {form.coverImage && (
            <Button variant="outlined" color="error" onClick={() => set("coverImage", "")}>
              Remove
            </Button>
          )}
        </Box>
        <TextField fullWidth size="small" placeholder="Or paste image URL…"
          value={form.coverImage} onChange={(e) => set("coverImage", e.target.value)} sx={{ mt: 1 }} />

        <Typography variant="subtitle2" sx={{ mt: 3 }}>Category</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
          {/* Categories are admin-managed; a post keeps a category that was
              deleted meanwhile, so show it too. */}
          {[...new Set([...categories, ...(form.category ? [form.category] : [])])].map((c) => (
            <Chip key={c} label={c} variant={form.category === c ? "filled" : "outlined"}
              color={form.category === c ? "primary" : "default"}
              onClick={() => set("category", form.category === c ? "" : c)} />
          ))}
          {isAdmin && !addingCategory && (
            <Chip label="+ New category" variant="outlined" onClick={() => setAddingCategory(true)} />
          )}
        </Box>
        {isAdmin && addingCategory && (
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <TextField size="small" autoFocus placeholder="Category name…" value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); onAddCategory(); }
                if (e.key === "Escape") { setAddingCategory(false); setNewCategory(""); }
              }} />
            <Button variant="contained" onClick={onAddCategory}
              disabled={savingCategory || !newCategory.trim()}>Add</Button>
            <Button onClick={() => { setAddingCategory(false); setNewCategory(""); }}>Cancel</Button>
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ mt: 3 }}>Tags</Typography>
        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
          <TextField size="small" placeholder="Add tag…" value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())} />
          <Button variant="contained" onClick={addTag}>+</Button>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
          {form.tags.map((t) => (
            <Chip key={t} label={t} onDelete={() => set("tags", form.tags.filter((x) => x !== t))} />
          ))}
        </Box>
      </Box>

      <PostPreview open={preview} onClose={() => setPreview(false)} post={form} />

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError("")}>
        <Alert severity="error" onClose={() => setError("")}>{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default BlogEditor;
